import React, {useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {InstrumentedWaveLoader} from '../../../utility/InstrumentedLoader';
import {SettingLabel} from '../components/SettingLabel';
import {
  BucketCustomization,
  SemanticLegendSettingsWithCategoricalConfig,
} from '../types';
import {BucketCustomizationInputWithCategoricalConfig} from './BucketCustomizationInputWithCategoricalConfig';
import {SemanticLegendBucketsActionsWithCategoricalConfig} from './SemanticLegendBucketsActionsWithCategoricalConfig';
import {SemanticLegendsPaletteWithCategoricalConfig} from './SemanticLegendsPaletteWithCategoricalConfig';

export type SemanticLegendBucketCustomizationProps = {
  buckets: BucketCustomization[];
  bucketCustomizations: Record<number, BucketCustomization>;
  minValue: number;
  updateSetting: (
    settings: Partial<SemanticLegendSettingsWithCategoricalConfig>
  ) => void;
  isLoadingBuckets: boolean;
};

export const SemanticLegendBucketsWithCategoricalConfig = ({
  buckets,
  bucketCustomizations,
  minValue,
  updateSetting,
  isLoadingBuckets,
}: SemanticLegendBucketCustomizationProps) => {
  const ids = useMemo(() => buckets.map(_i => uuidv4()), [buckets]);

  const updateBucketMax = (
    settings: Partial<BucketCustomization>,
    index: number
  ) => {
    const newBuckets = [...buckets];
    newBuckets[index] = {
      ...newBuckets[index],
      bucketMax: settings.bucketMax,
    };
    if (index < buckets.length - 1) {
      newBuckets[index + 1].bucketMin = settings.bucketMax;
    }
    updateSetting({buckets: newBuckets});
  };

  const updateBucketColor = (
    settings: Partial<BucketCustomization>,
    index: number
  ) => {
    const newBucketCustomizations = {...bucketCustomizations};
    newBucketCustomizations[index] = {
      ...newBucketCustomizations[index],
      color: settings.color,
    };
    updateSetting({bucketCustomizations: newBucketCustomizations});
  };

  const bucketCustomizationList = buckets?.map((bucket, index) => (
    <li key={ids[index]}>
      <BucketCustomizationInputWithCategoricalConfig
        bucket={bucket}
        bucketCustomization={bucketCustomizations[index] ?? {}}
        bucketMin={index === 0 ? minValue : (bucket.bucketMin ?? 0)}
        index={index}
        nextBucketMax={
          index === buckets.length - 1
            ? (bucket.bucketMax ?? 0)
            : (buckets[index + 1]?.bucketMax ?? 0)
        }
        numberOfBuckets={buckets?.length}
        updateBucketColor={(settings: Partial<BucketCustomization>) =>
          updateBucketColor(settings, index)
        }
        updateBucketMax={(settings: Partial<BucketCustomization>) =>
          updateBucketMax(settings, index)
        }
      />
    </li>
  ));

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-8" data-test="buckets-section">
      <div className="flex w-full gap-8">
        <div className="flex w-full gap-8">
          <SettingLabel text="Buckets" className="w-fit" />
          {isLoadingBuckets && (
            <InstrumentedWaveLoader name="buckets-loading" size="small" />
          )}
        </div>
        <SemanticLegendsPaletteWithCategoricalConfig />
        <SemanticLegendBucketsActionsWithCategoricalConfig
          setIsOpen={setIsOpen}
          isOpen={isOpen}
        />
      </div>
      <ul className="flex flex-col gap-8">{bucketCustomizationList}</ul>
    </div>
  );
};
